import { create } from 'zustand';
import type {
  CartsReportDto,
  CustomReportDefinition,
  CustomReportResultDto,
  GeographyReportDto,
  ProfitReportDto,
  ReportFilters,
  ReportRangePreset,
  ReportViewDto,
  ReturnsReportDto,
  ShippingReportDto,
  StaffReportDto,
  StockValueDto,
  CustomersReportDto,
  CustomerValueReportDto,
  DateRange,
  DiscountUsageDto,
  FinanceReportDto,
  FunnelReportDto,
  OrdersReportDto,
  ProductPerformanceDto,
  ProductPerformanceSort,
  RetentionReportDto,
  ReportExportKey,
  SalesPointDto,
  SourcesReportDto,
  TopProductDto,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { addDays, lastDays, localDay, monthToDate } from '@/lib/date-range';
import { saveFile } from '@/lib/download';
import { useUiStore } from '@/i18n';

/**
 * Reports (API → Store → Render, ADR-008).
 *
 * One range drives every report, so switching tabs never silently changes the
 * period being read — the single most confusing thing a reports screen can do.
 * Each report loads on demand, and only the latest load may write — a slow
 * finance query cannot land under the orders heading after a tab switch.
 * Exports run on their own, one busy state each, beside whatever is loading.
 */

export type ReportTab =
  | 'orders'
  | 'funnel'
  | 'products'
  | 'performance'
  | 'customers'
  | 'retention'
  | 'value'
  | 'finance'
  | 'discounts'
  | 'sources'
  // Reports v3 (ADR-118, ADR-120, ADR-122).
  | 'profit'
  | 'stock'
  | 'geography'
  | 'shipping'
  | 'returns'
  | 'carts'
  | 'staff'
  | 'custom';

/** The reports the filters narrow (ADR-119); the rest take the period alone. */
export const FILTERABLE_TABS: readonly ReportTab[] = ['orders', 'products', 'customers', 'finance', 'discounts', 'sources', 'profit', 'geography', 'shipping', 'returns', 'custom'];

/** A custom report to start from: revenue by city. */
export const DEFAULT_CUSTOM: CustomReportDefinition = {
  dataset: 'orders',
  columns: ['city', 'orders', 'revenue', 'averageOrder'],
  groupBy: ['city'],
  sort: { column: 'revenue', direction: 'desc' },
};

interface ReportsState {
  range: DateRange;
  loading: boolean;
  error: string | null;
  /** The exports being built, by `exportId` — each button minds its own. */
  exporting: string[];

  sales: SalesPointDto[];
  orders: OrdersReportDto | null;
  products: TopProductDto[];
  customers: CustomersReportDto | null;
  finance: FinanceReportDto | null;
  discounts: DiscountUsageDto[];
  /** Sales by source (ADR-097). */
  sources: SourcesReportDto | null;
  /** Reports v2 (ADR-108): the store's own visits, per product, and whether buyers return. */
  funnel: FunnelReportDto | null;
  performance: ProductPerformanceDto | null;
  performanceSort: ProductPerformanceSort;
  retention: RetentionReportDto | null;
  /** What customers are worth (ADR-109). */
  customerValue: CustomerValueReportDto | null;
  setPerformanceSort: (sort: ProductPerformanceSort) => void;

  /** What narrows the order reports (ADR-119), and the words each chosen value reads as. */
  filters: ReportFilters;
  filterLabels: Record<string, string>;
  setFilters: (filters: ReportFilters, labels?: Record<string, string>) => void;
  /** Names for chosen values found later — nothing reloads. */
  addLabels: (labels: Record<string, string>) => void;

  profit: ProfitReportDto | null;
  stock: StockValueDto | null;
  geography: GeographyReportDto | null;
  shipping: ShippingReportDto | null;
  returns: ReturnsReportDto | null;
  carts: CartsReportDto | null;
  staff: StaffReportDto | null;
  /** The custom report being built, and what it last returned (ADR-122). */
  custom: CustomReportDefinition;
  customResult: CustomReportResultDto | null;
  setCustom: (definition: CustomReportDefinition) => void;
  exportCustom: (format: ExportFormat) => Promise<void>;
  backfillCosts: () => Promise<number | null>;

  /** Saved views (ADR-119): mine and the team's. */
  views: ReportViewDto[];
  loadViews: () => Promise<void>;
  saveView: (input: { name: string; report: ReportTab; shared: boolean; existing?: string }) => Promise<boolean>;
  removeView: (id: string) => Promise<void>;
  /** The view's period, filters and (for a custom report) definition; the page opens its tab. */
  applyView: (view: ReportViewDto) => void;

  setRange: (range: DateRange) => void;
  load: (tab: ReportTab) => Promise<void>;
  exportReport: (key: ReportExportKey, format: ExportFormat) => Promise<void>;
}

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

/** One export in flight: a report in a format. */
export function exportId(key: ReportExportKey, format: ExportFormat): string {
  return `${key}.${format}`;
}

// Only the latest load may write: switching tabs or ranges quickly must not let
// an older, slower report land under the newer heading.
let loadRequest = 0;

export const useReportsStore = create<ReportsState>((set, get) => ({
  // This month so far, on the operator's calendar rather than UTC's.
  range: monthToDate(),
  loading: false,
  error: null,
  exporting: [],

  sales: [],
  orders: null,
  products: [],
  customers: null,
  finance: null,
  discounts: [],
  sources: null,
  funnel: null,
  performance: null,
  performanceSort: 'revenue',
  retention: null,
  customerValue: null,
  filters: {},
  filterLabels: {},
  profit: null,
  stock: null,
  geography: null,
  shipping: null,
  returns: null,
  carts: null,
  staff: null,
  custom: DEFAULT_CUSTOM,
  customResult: null,
  views: [],

  setFilters(filters, labels) {
    set((state) => ({ filters, filterLabels: { ...state.filterLabels, ...labels }, ...cleared() }));
  },

  addLabels(labels) {
    set((state) => ({ filterLabels: { ...state.filterLabels, ...labels } }));
  },

  setCustom(definition) {
    set({ custom: definition, customResult: null });
  },

  async exportCustom(format) {
    const { range, filters, custom } = get();
    const id = `custom.${format}`;
    if (get().exporting.includes(id)) return;
    set((state) => ({ exporting: [...state.exporting, id], error: null }));
    try {
      const locale = useUiStore.getState().locale;
      await saveFile(() => withSession(() => adminApi.reports.exportCustom(format, custom, range, filters, locale)), `report-custom.${format}`);
    } catch (error) {
      set({ error: errorMessage(error, 'Could not build the export.') });
    } finally {
      set((state) => ({ exporting: state.exporting.filter((entry) => entry !== id) }));
    }
  },

  async backfillCosts() {
    try {
      const { lines } = await withSession(() => adminApi.reports.backfillCosts());
      set({ profit: null });
      void get().load('profit');
      return lines;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not fill the costs.') });
      return null;
    }
  },

  async loadViews() {
    try {
      set({ views: await withSession(() => adminApi.reports.views.list()) });
    } catch {
      // The reports work without their saved views; the menu stays empty.
    }
  },

  async saveView({ name, report, shared, existing }) {
    const { range, filters, custom } = get();
    const body = {
      name,
      report,
      range: { preset: presetFor(range), from: range.from, to: range.to },
      filters,
      custom: report === 'custom' ? custom : null,
      shared,
    };
    try {
      await withSession(() => (existing ? adminApi.reports.views.update(existing, body) : adminApi.reports.views.create(body)));
      await get().loadViews();
      return true;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not save the view.') });
      return false;
    }
  },

  async removeView(id) {
    try {
      await withSession(() => adminApi.reports.views.remove(id));
      set((state) => ({ views: state.views.filter((view) => view.id !== id) }));
    } catch (error) {
      set({ error: errorMessage(error, 'Could not remove the view.') });
    }
  },

  applyView(view) {
    set({
      range: rangeOf(view.range),
      filters: view.filters,
      ...(view.custom ? { custom: view.custom } : {}),
      ...cleared(),
    });
  },

  setPerformanceSort(sort) {
    if (get().performanceSort === sort) return;
    set({ performanceSort: sort, performance: null });
    void get().load('performance');
  },

  setRange(range) {
    // The same days again is no change: clearing them would blank the screen
    // with nothing to load it back.
    const current = get().range;
    if (current.from === range.from && current.to === range.to) return;

    // Clearing the loaded reports is deliberate: showing July's table under an
    // August heading for even a moment is worse than showing nothing.
    set({ range, funnel: null, performance: null, retention: null, customerValue: null, carts: null, staff: null, ...cleared() });
  },

  async load(tab) {
    const { range, filters } = get();
    const request = ++loadRequest;
    set({ loading: true, error: null });

    try {
      let loaded: Partial<ReportsState>;
      switch (tab) {
        case 'orders': {
          const [orders, sales] = await Promise.all([
            withSession(() => adminApi.reports.orders(range, filters)),
            withSession(() => adminApi.reports.salesByDay(range, filters)),
          ]);
          loaded = { orders, sales };
          break;
        }
        case 'products':
          loaded = { products: await withSession(() => adminApi.reports.topProducts(range, filters)) };
          break;
        case 'customers':
          loaded = { customers: await withSession(() => adminApi.reports.customers(range, filters)) };
          break;
        case 'finance':
          loaded = { finance: await withSession(() => adminApi.reports.finance(range, filters)) };
          break;
        case 'discounts':
          loaded = { discounts: await withSession(() => adminApi.reports.discounts(range, filters)) };
          break;
        case 'sources':
          loaded = { sources: await withSession(() => adminApi.reports.sources(range, filters)) };
          break;
        case 'funnel':
          loaded = { funnel: await withSession(() => adminApi.reports.funnel(range)) };
          break;
        case 'performance': {
          const locale = useUiStore.getState().locale;
          loaded = { performance: await withSession(() => adminApi.reports.productPerformance(range, { sort: get().performanceSort, limit: 100, locale })) };
          break;
        }
        case 'retention':
          loaded = { retention: await withSession(() => adminApi.reports.retention(range)) };
          break;
        case 'value':
          loaded = { customerValue: await withSession(() => adminApi.reports.customerValue(range)) };
          break;
        case 'profit':
          loaded = { profit: await withSession(() => adminApi.reports.profit(range, filters, useUiStore.getState().locale)) };
          break;
        case 'stock':
          loaded = { stock: await withSession(() => adminApi.reports.stockValue(useUiStore.getState().locale)) };
          break;
        case 'geography':
          loaded = { geography: await withSession(() => adminApi.reports.geography(range, filters)) };
          break;
        case 'shipping':
          loaded = { shipping: await withSession(() => adminApi.reports.shipping(range, filters)) };
          break;
        case 'returns':
          loaded = { returns: await withSession(() => adminApi.reports.returns(range, filters)) };
          break;
        case 'carts':
          loaded = { carts: await withSession(() => adminApi.reports.carts(range)) };
          break;
        case 'staff':
          loaded = { staff: await withSession(() => adminApi.reports.staff(range)) };
          break;
        case 'custom':
          loaded = { customResult: await withSession(() => adminApi.reports.custom(get().custom, range, filters, useUiStore.getState().locale)) };
          break;
      }
      if (request !== loadRequest) return;
      set({ ...loaded, loading: false });
    } catch (error) {
      if (request !== loadRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load this report.') });
    }
  },

  async exportReport(key, format) {
    const { range } = get();
    // The filters go with the reports they narrow, and nowhere else (ADR-119).
    const filters = FILTERABLE_EXPORTS.includes(key) ? get().filters : {};
    const id = exportId(key, format);
    if (get().exporting.includes(id)) return;
    set((state) => ({ exporting: [...state.exporting, id], error: null }));

    // Three formats, one table on the server — the numbers cannot disagree.
    const fetchers = {
      csv: () => adminApi.reports.exportCsv(key, range, filters),
      xlsx: () => adminApi.reports.exportXlsx(key, range, filters),
      pdf: () => adminApi.reports.exportPdf(key, range, filters),
    } as const;

    try {
      await saveFile(() => withSession(fetchers[format]), `report-${key}.${format}`);
    } catch (error) {
      set({ error: errorMessage(error, 'Could not build the export.') });
    } finally {
      set((state) => ({ exporting: state.exporting.filter((entry) => entry !== id) }));
    }
  },
}));

/** Every report the period or the filters feed, emptied together: no table outlives what it was read for. */
function cleared(): Partial<ReportsState> {
  return {
    orders: null,
    customers: null,
    finance: null,
    sales: [],
    products: [],
    discounts: [],
    sources: null,
    profit: null,
    geography: null,
    shipping: null,
    returns: null,
    customResult: null,
  };
}

const FILTERABLE_EXPORTS: readonly ReportExportKey[] = ['sales', 'orders', 'top-products', 'customers', 'finance', 'discounts', 'sources', 'profit', 'geography', 'shipping', 'returns'];

/** A saved view keeps the rule ("last 30 days"), not the days, unless the days are what was chosen. */
function presetFor(range: DateRange): ReportRangePreset {
  const today = localDay();
  if (range.to === today) {
    if (range.from === addDays(today, -6)) return 'last7';
    if (range.from === addDays(today, -29)) return 'last30';
    if (range.from === addDays(today, -89)) return 'last90';
    if (range.from === `${today.slice(0, 8)}01`) return 'thisMonth';
    if (range.from === `${today.slice(0, 5)}01-01`) return 'thisYear';
  }
  const lastMonth = rangeOf({ preset: 'lastMonth', from: null, to: null });
  if (range.from === lastMonth.from && range.to === lastMonth.to) return 'lastMonth';
  return 'fixed';
}

/** A view's rule, as today's days. */
export function rangeOf(range: { preset: ReportRangePreset; from: string | null; to: string | null }): DateRange {
  const today = localDay();
  switch (range.preset) {
    case 'last7':
      return lastDays(7);
    case 'last30':
      return lastDays(30);
    case 'last90':
      return lastDays(90);
    case 'thisMonth':
      return monthToDate();
    case 'thisYear':
      return { from: `${today.slice(0, 5)}01-01`, to: today };
    case 'lastMonth': {
      const firstOfThis = `${today.slice(0, 8)}01`;
      const end = addDays(firstOfThis, -1);
      return { from: `${end.slice(0, 8)}01`, to: end };
    }
    case 'fixed':
      return range.from && range.to ? { from: range.from, to: range.to } : monthToDate();
  }
}
